// Os widgets do ecrã inicial (IDEA-029).
//
// Cada um é um cartão pequeno e independente. Todos leem do mesmo pedido
// (`GET /home`), por isso não há seis shimmers a chegar em ordens diferentes.
//
// Regra que os mantém honestos: um widget sem nada para dizer não aparece.
// Um "próximo evento: nenhum" ocupa o mesmo espaço que um evento a sério e não
// vale nada — é ruído num ecrã que devia ser todo conteúdo.

import type { ReactNode } from "react";
import { Link } from "react-router";
import { motion, useReducedMotion } from "framer-motion";
import type { HomeSummary } from "@historia/shared/contracts";
import { GAME_LABELS } from "@historia/shared/validation";
import { daysBetween, longDate as dataLonga, todayIso, weekday } from "@historia/shared/dates";

const numero = new Intl.NumberFormat("pt-PT");

export function Widgets({ dados }: { dados: HomeSummary }) {
  const reduzir = useReducedMotion();
  const { numbers, nextEvent, latestPhoto, anniversary, pendingGames } = dados;

  return (
    <motion.div
      initial="hidden"
      animate="show"
      transition={{ staggerChildren: reduzir ? 0 : 0.06 }}
      className="grid grid-cols-2 gap-3 sm:grid-cols-4"
    >
      {/* Dias juntos — o widget grande, e o que o pedido nomeou. */}
      <Cartao className="col-span-2 sm:col-span-2" destaque>
        <p className="font-titulo text-5xl leading-none text-ouro-600 sm:text-6xl">
          {numero.format(numbers.daysTogether)}
        </p>
        <p className="mt-2 text-sm text-tinta-700">dias juntos, e a contar</p>
      </Cartao>

      {/* O aniversário. */}
      <Cartao className="col-span-2 sm:col-span-2">
        <p className="text-3xl" aria-hidden>
          💛
        </p>
        <p className="mt-1 font-titulo text-2xl text-tinta-800">
          {anniversary.daysRemaining === 0
            ? "É hoje!"
            : `${anniversary.daysRemaining} ${anniversary.daysRemaining === 1 ? "dia" : "dias"}`}
        </p>
        <p className="mt-1 text-xs text-tinta-600">
          {anniversary.daysRemaining === 0
            ? `O nosso ${anniversary.years}.º aniversário`
            : `até ao ${anniversary.years}.º aniversário`}
        </p>
        <Atalho para="/para-sempre">Para sempre…</Atalho>
      </Cartao>

      {/* Próximo evento — só quando há um. */}
      {nextEvent && (
        <Cartao className="col-span-2">
          <Etiqueta>A seguir</Etiqueta>
          <p className="mt-1 flex items-center gap-2 font-semibold text-tinta-800">
            <span aria-hidden>{nextEvent.evento.icone}</span>
            <span className="truncate">{nextEvent.evento.titulo}</span>
          </p>
          <p className="mt-1 text-xs capitalize text-tinta-600">
            {descreverQuando(nextEvent.data)}
            {!nextEvent.evento.dia_inteiro && nextEvent.evento.hora_inicio
              ? ` · ${nextEvent.evento.hora_inicio}`
              : ""}
          </p>
          <Atalho para="/calendario">Ver o calendário</Atalho>
        </Cartao>
      )}

      {/* Rondas à espera — só quando é a nossa vez. */}
      {pendingGames.length > 0 && (
        <Cartao className="col-span-2">
          <Etiqueta>É a tua vez</Etiqueta>
          <ul className="mt-1 flex flex-col gap-1">
            {pendingGames.map((jogo) => (
              <li key={jogo.jogo}>
                <Link
                  to={`/jogos/${jogo.jogo}`}
                  className="font-semibold text-tinta-800 hover:text-ouro-600"
                >
                  {GAME_LABELS[jogo.jogo]} →
                </Link>
              </li>
            ))}
          </ul>
          <p className="mt-1 text-xs text-tinta-600">
            {pendingGames.length === 1 ? "Há uma ronda" : "Há rondas"} à tua espera.
          </p>
        </Cartao>
      )}

      {/* A última memória. */}
      {latestPhoto?.src && (
        <Cartao className="col-span-2 overflow-hidden !p-0" semPadding>
          <Link to="/album" className="group block">
            <img
              src={latestPhoto.src}
              alt={latestPhoto.legenda ?? "A memória mais recente"}
              loading="lazy"
              decoding="async"
              className="h-36 w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <span className="block px-4 py-3">
              <Etiqueta>A última memória</Etiqueta>
              <span className="mt-0.5 block truncate text-sm text-tinta-700">
                {latestPhoto.legenda ?? "no álbum"}
              </span>
            </span>
          </Link>
        </Cartao>
      )}

      {/* Os números. Só aparecem os que já são maiores que zero — um "0 lugares"
          não é um número afectuoso, é uma tarefa por fazer. */}
      <Numero valor={numbers.milestones} etiqueta="marcos" para="/timeline" icone="🌟" />
      <Numero valor={numbers.photos} etiqueta="fotografias" para="/album" icone="📷" />
      <Numero valor={numbers.places} etiqueta="lugares" para="/mapa" icone="📍" />
      <Numero valor={numbers.countries} etiqueta="países" para="/mapa" icone="🌍" />
      <Numero valor={numbers.trips} etiqueta="viagens" para="/timeline" icone="✈️" />
      <Numero valor={numbers.illustrations} etiqueta="ilustrações" para="/conto" icone="🎨" />
      <Numero valor={numbers.messages} etiqueta="mensagens" para="/para-sempre" icone="💌" />
      <Numero valor={numbers.events} etiqueta="eventos" para="/calendario" icone="📅" />
    </motion.div>
  );
}

/** "hoje", "amanhã", "daqui a 5 dias" ou a data por extenso. */
function descreverQuando(iso: string): string {
  const dias = daysBetween(todayIso(), iso);
  if (dias === 0) return "hoje";
  if (dias === 1) return "amanhã";
  if (dias > 1 && dias < 7) return `${weekday(iso)}, daqui a ${dias} dias`;
  return dataLonga(iso);
}

const aparecer = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0 },
};

function Cartao({
  children,
  className = "",
  destaque = false,
  semPadding = false,
}: {
  children: ReactNode;
  className?: string;
  destaque?: boolean;
  semPadding?: boolean;
}) {
  return (
    <motion.div
      variants={aparecer}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className={`rounded-2xl border ${semPadding ? "" : "p-4"} ${
        destaque
          ? "border-ouro/40 bg-creme-50 shadow-[0_14px_34px_-22px_rgba(201,162,39,0.8)]"
          : "border-ouro/20 bg-creme-50/80"
      } ${className}`}
    >
      {children}
    </motion.div>
  );
}

function Etiqueta({ children }: { children: ReactNode }) {
  return (
    <span className="text-[11px] font-semibold uppercase tracking-wide text-tinta-500">
      {children}
    </span>
  );
}

function Atalho({ para, children }: { para: string; children: ReactNode }) {
  return (
    <Link
      to={para}
      className="mt-2 inline-block text-xs font-semibold text-ouro-600 hover:underline"
    >
      {children}
    </Link>
  );
}

function Numero({
  valor,
  etiqueta,
  para,
  icone,
}: {
  valor: number;
  etiqueta: string;
  para: string;
  icone: string;
}) {
  if (valor <= 0) return null;

  return (
    <motion.div variants={aparecer} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}>
      <Link
        to={para}
        className="flex h-full flex-col items-center justify-center rounded-2xl border border-ouro/20 bg-creme-50/80 px-3 py-4 text-center transition-colors hover:border-ouro/50 hover:bg-creme-50"
      >
        <span className="text-lg" aria-hidden>
          {icone}
        </span>
        <span className="mt-1 font-titulo text-2xl text-tinta-800">{numero.format(valor)}</span>
        <span className="text-xs text-tinta-600">{etiqueta}</span>
      </Link>
    </motion.div>
  );
}
